import React from 'react'
import { TransformedResolvedPerson } from '../../../utils/networkTransformUtils'
import { MapExtra } from '../../../utils/MapExtra'
import { CompanyDto } from '../../../api/company/models/CompanyDto'
import { PersonLiteDto } from '../../../api/person/models/PersonDto'
import { CandidateRecommendationDto } from '../../../api/role/models/getRecommendedCandidatesForRole/GetRecommendedCandidatesForRole'
import { formatPersonName } from '../../../utils/personUtils'
import { formatDuration } from '../../../utils/dateUtils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBuilding } from '@fortawesome/free-solid-svg-icons'
import Badge from '../../common/badge/Badge'
import { BadgeColor } from '../../common/badge/badgeTypes'

interface Props {
    connections?: TransformedResolvedPerson
    companiesById: MapExtra<string, CompanyDto>
    seedPersonLitesById: MapExtra<string, PersonLiteDto>
    recommendationsBySeedId: MapExtra<string, CandidateRecommendationDto>
    title?: string
}

export default function CandidateConnectionsTrustedCircle({
    connections,
    companiesById,
    seedPersonLitesById,
    recommendationsBySeedId,
    title = 'Has worked with',
}: Props): React.JSX.Element | null {
    if (!connections?.companies?.length) return null

    return (
        <div className="rounded-lg border-ctxt-blue bg-ctxt-blue/10 text-gray-800 p-3">
            <div className="text-sm font-bold text-gray-900 mb-2">{title}</div>
            <div className="space-y-3">
                {connections.companies.map((co) => {
                    const company = companiesById.get(co.companyId)
                    if (!company) return null
                    return (
                        <div key={co.companyId} className="text-sm">
                            <div className="flex items-start gap-2">
                                <FontAwesomeIcon
                                    icon={faBuilding}
                                    className="mt-0.5 h-4 w-4 text-ctxt-blue"
                                />
                                <div>
                                    <span className="font-bold text-ctxt-blue">
                                        {company.name}
                                    </span>
                                </div>
                            </div>

                            <div className="mt-2 ml-1 space-y-2">
                                {co.connections.map((conn) => {
                                    const seed = seedPersonLitesById.get(
                                        conn.seedPersonId,
                                    )
                                    const rec = recommendationsBySeedId.get(
                                        conn.seedPersonId,
                                    )
                                    const { fromTo, duration } = formatDuration(
                                        conn.overallStart,
                                        conn.overallEnd,
                                    )

                                    return (
                                        <div
                                            key={`${co.companyId}-${conn.seedPersonId}`}
                                            className="flex gap-2"
                                        >
                                            <div className="flex-1 flex flex-col">
                                                <div className="flex items-center gap-2">
                                                    <span>
                                                        {seed
                                                            ? formatPersonName(
                                                                  seed,
                                                              )
                                                            : 'Unknown user'}
                                                    </span>
                                                    {rec && (
                                                        <Badge
                                                            color={
                                                                BadgeColor.Violet
                                                            }
                                                        >
                                                            Recommended
                                                        </Badge>
                                                    )}
                                                </div>
                                                <div className="text-gray-500 text-xs italic mt-0.5">
                                                    · {fromTo} · {duration}
                                                </div>
                                                {rec?.evalComment && (
                                                    <div className="relative inline-block w-full mt-2 pl-2">
                                                        <div className="bg-white text-sm text-gray-700 p-3 rounded-xl shadow">
                                                            <div>
                                                                {
                                                                    rec.evalComment
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
